import {Component, computed, effect, ElementRef, inject, input, output} from '@angular/core';
import {DatePipe, DecimalPipe} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';
import {CustomerProductSummary} from '../../../../../../../api';
import {ElciaButton} from '../../../../../../components/buttons/button/button';
import {isOneShotProduct} from '../../customer-product.utils';
import {Dot} from '../../../../../../components/dot/dot';
import {CanAccessFinances} from '../../../../../../directives/can-access-finances';

@Component({
  selector: 'elcia-customer-product-item',
  imports: [DatePipe, DecimalPipe, TranslatePipe, ElciaButton, Dot, CanAccessFinances],
  templateUrl: './customer-product-item.html',
  styleUrl: './customer-product-item.scss',
  // items-start so the status dot aligns with the first text row, not the block centroid
  host: {
    class: 'flex items-start gap-3 px-3 py-2.5 rounded transition-colors duration-200 hover:bg-primary-50',
    '[class.ring-2]': 'isHighlighted()',
    '[class.ring-primary-200]': 'isHighlighted()',
    '[class.bg-primary-50]': 'isHighlighted()',
  },
})
export class CustomerProductItem {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  product = input.required<CustomerProductSummary>();
  isHighlighted = input<boolean>(false);
  highlightTick = input<number>(0);
  onEdit = output<CustomerProductSummary>();
  onDelete = output<CustomerProductSummary>();

  // A timeline click can target a licence scrolled far out of view, where the highlight alone would go
  // unseen. 'nearest' scrolls the least it takes, so a row already on screen does not move.
  private readonly revealWhenHighlighted = effect(() => {
    // Read before the guard: clicking the same entry twice leaves isHighlighted at true, so the tick is
    // the only thing telling this effect to run again.
    this.highlightTick();

    if (!this.isHighlighted()) return;

    this.host.nativeElement.scrollIntoView({
      behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'nearest',
    });
  });

  protected readonly isOneShot = computed(() => isOneShotProduct(this.product()));

  // Highlight only recurring products that are active but not yet attached to a licence server:
  // a one-shot is never expected on a server, and an inactive product no longer needs one.
  protected readonly showUnassignedWarning = computed(() =>
    !this.isOneShot() && this.product().isActive && this.product().licenceServerId == null);

  protected edit(): void {
    this.onEdit.emit(this.product());
  }

  protected delete(): void {
    this.onDelete.emit(this.product());
  }
}
